import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { NotificationPopupService } from './notification-popup.service';
import { Subject, takeUntil } from 'rxjs';

interface PopupItem {
    notification: any;
    id: string;
    timer?: any;
}

@Component({
    selector: 'notification-popup',
    templateUrl: './notification-popup.component.html',
    styleUrls: ['./notification-popup.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [NgIf, NgFor, NgClass, MatIconModule, MatButtonModule]
})
export class NotificationPopupComponent implements OnInit, OnDestroy {
    popups: PopupItem[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationPopupService: NotificationPopupService,
        private _router: Router
    ) {}

    ngOnInit(): void {
        // Subscribe to popup notifications
        this._notificationPopupService.popup$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((popup) => {
                this.addPopup(popup);
            });
    }

    ngOnDestroy(): void {
        // Clear all timers
        this.popups.forEach(popup => {
            if (popup.timer) {
                clearTimeout(popup.timer);
            }
        });

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Add a new popup
     */
    addPopup(popup: any): void {
        const popupItem: PopupItem = {
            notification: popup.notification,
            id: popup.id
        };

        // Add to array
        this.popups.unshift(popupItem);

        // Auto-remove after 5 seconds
        popupItem.timer = setTimeout(() => {
            this.removePopup(popupItem.id);
        }, 5000);

        // Play sound
        this.playNotificationSound();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove a popup
     */
    removePopup(id: string): void {
        const index = this.popups.findIndex(p => p.id === id);
        if (index !== -1) {
            const popup = this.popups[index];
            if (popup.timer) {
                clearTimeout(popup.timer);
            }
            this.popups.splice(index, 1);
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Handle popup click
     */
    onPopupClick(notification: any): void {
        if (notification.link) {
            // Use router navigation by default
            if (notification.useRouter !== false) {
                this._router.navigateByUrl(notification.link);
            } else {
                window.location.href = notification.link;
            }
        }
        // Remove popup after click
        const popup = this.popups.find(p => p.notification.id === notification.id);
        if (popup) {
            this.removePopup(popup.id);
        }
    }

    /**
     * Play notification sound
     */
    private playNotificationSound(): void {
        try {
            // Try to play custom sound file
            const audio = new Audio('/assets/sounds/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {
                // Fallback to beep if sound file doesn't exist
                this.playBeepSound();
            });
        } catch (error) {
            // Fallback to beep
            this.playBeepSound();
        }
    }

    /**
     * Play beep sound using Web Audio API
     */
    private playBeepSound(): void {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800; // 800Hz tone
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
            console.warn('Could not play notification sound:', error);
        }
    }

    /**
     * Track by function for ngFor
     */
    trackByFn(index: number, item: PopupItem): string {
        return item.id;
    }
}

